import type { RangePickerProps } from 'antd/es/date-picker'
import dayjs, { type Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

import type {
  DurationDiscrepancyApi,
  ReconciliationApiItem,
  ReconciliationRow,
  ReconciliationRowActualTime,
  ReconciliationRowTime
} from '../../Reports/types/reconciliation'

export const RECONCILIATION_DURATION_DISCREPANCY_UI_TO_API: Record<
  string,
  DurationDiscrepancyApi
> = {
  lt5: 'LESS_THAN_5_MINUTES',
  '5to15': 'BETWEEN_5_AND_15_MINUTES',
  '15to30': 'BETWEEN_15_AND_30_MINUTES',
  gt30: 'MORE_THAN_30_MINUTES'
}

export const RECONCILIATION_MAX_PAST_MONTHS = 12
export const RECONCILIATION_MAX_RANGE_MONTHS = 6

export const getReconciliationRangeDisabledDate: NonNullable<
  RangePickerProps['disabledDate']
> = (current, info) => {
  const today = dayjs().startOf('day')
  const c = current.startOf('day')

  if (c.isAfter(today, 'day')) {
    return true
  }

  const earliest = today
    .subtract(RECONCILIATION_MAX_PAST_MONTHS, 'month')
    .startOf('day')
  if (c.isBefore(earliest, 'day')) {
    return true
  }

  const from = info?.from
  if (!from) {
    return false
  }

  const f = from.startOf('day')
  const windowStart = f.subtract(RECONCILIATION_MAX_RANGE_MONTHS, 'month')
  const windowEnd = f.add(RECONCILIATION_MAX_RANGE_MONTHS, 'month')

  if (c.isBefore(windowStart, 'day') || c.isAfter(windowEnd, 'day')) {
    return true
  }

  return false
}

export function isValidReconciliationDateRange(
  start: Dayjs,
  end: Dayjs
): boolean {
  const s = start.startOf('day')
  const e = end.startOf('day')
  const today = dayjs().startOf('day')
  if (e.isBefore(s, 'day')) {
    return false
  }
  const earliestStart = today
    .subtract(RECONCILIATION_MAX_PAST_MONTHS, 'month')
    .startOf('day')
  if (s.isBefore(earliestStart, 'day')) {
    return false
  }
  if (e.isAfter(today, 'day')) {
    return false
  }
  const maxEnd = s.add(RECONCILIATION_MAX_RANGE_MONTHS, 'month').startOf('day')
  if (e.isAfter(maxEnd, 'day')) {
    return false
  }
  return true
}

export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) {
    return `${m}m`
  }
  if (m === 0) {
    return `${h}h`
  }
  return `${h}h ${m}m`
}

export function formatTime(
  isoString: string,
  timeZone?: string | null
): string {
  if (!isoString) {
    return ''
  }
  if (timeZone) {
    return dayjs.utc(isoString).tz(timeZone).format('HH:mm')
  }
  return dayjs(isoString).format('HH:mm')
}

export function formatTimeRange(rowTime: ReconciliationRowTime): string {
  return `${dayjs(rowTime.startTime).format('HH:mm')}–${dayjs(rowTime.endTime).format('HH:mm')} · ${formatMinutes(rowTime.durationMinutes)}`
}

export function formatDisplayName(
  name: string | { first?: string; last?: string } | null | undefined
): string {
  if (name == null) {
    return ''
  }
  if (typeof name === 'string') {
    return name
  }
  const first = name.first ?? ''
  const last = name.last ?? ''
  return `${first} ${last}`.trim()
}

export function isCompleteActualTime(
  actualTime: ReconciliationRowActualTime | null
): actualTime is ReconciliationRowActualTime & {
  endTime: string
  durationMinutes: number
} {
  if (!actualTime) {
    return false
  }
  return actualTime.endTime != null && actualTime.durationMinutes != null
}

export function rowKey(r: ReconciliationRow): string {
  if (r.rotaInstanceId) {
    return `${r.rotaInstanceId}:${r.teamMemberId}`
  }
  return `${r.date}:${r.startTimeIso}:${r.teamMemberId}`
}

export function mapReconciliationApiItemToRow(
  item: ReconciliationApiItem
): ReconciliationRow | null {
  const planned = item.plannedTime
  if (!planned?.start || !item.teamMember?.id || !item.instanceType?.id) {
    return null
  }

  const plannedTime = {
    startTime: planned.start,
    endTime: planned.end ?? planned.start,
    durationMinutes: planned.duration ?? 0
  }

  const actualTime: ReconciliationRowActualTime | null = item.actualTime?.start
    ? {
        startTime: item.actualTime.start,
        endTime: item.actualTime.end ?? null,
        durationMinutes: item.actualTime.duration ?? null
      }
    : null

  const isCanceled = item.instanceStatus === 'CANCELLED'

  const w = item.warnings
  const warningCount = item.warningsCount ?? 0

  return {
    rotaInstanceId: item._id,
    startTimeIso: planned.start,
    teamMemberId: item.teamMember.id,
    status: item.status,
    date: item.timezone
      ? dayjs.utc(planned.start).tz(item.timezone).format('YYYY-MM-DD')
      : dayjs(planned.start).format('YYYY-MM-DD'),
    teamMember: {
      id: item.teamMember.id,
      name: item.teamMember.name,
      avatarUrl: item.teamMember.picture || null
    },
    serviceUser: item.subject
      ? {
          id: item.subject.id,
          name: item.subject.name,
          avatarUrl: item.subject.picture || null
        }
      : null,
    eventType: {
      id: item.instanceType.id,
      name: item.instanceType.name
    },
    plannedTime,
    actualTime,
    isCanceled,
    durationDiscrepancy: item.durationDiscrepancy ?? null,
    warningCount,
    rawWarnings: w ?? null,
    payRate: item.payRate ?? null,
    payRateType: item.payRateType ?? null,
    paymentCardName: item.paymentCardName ?? null,
    isCustomInstancePayRate: item.isCustomInstancePayRate ?? null,
    paidTime: item.paidTime ?? null,
    payAmount: item.payAmount ?? null,
    hasShiftRate: item.payRateType === 'FIXED',
    dontPayReason: item.dontPayReason ?? null,
    payBy: item.payBy ?? null,
    cancelledBy: item.cancelledBy ?? null,
    cancelledReason: item.cancelledReason ?? null,
    cancelledFinancialImpact: item.cancelledFinancialImpact ?? null,
    cancelledPreventable: item.cancelledPreventable ?? null,
    cancelledNotes: item.cancelledNotes ?? null,
    timezone: item.timezone ?? null
  }
}

export function combineDateAndTime(
  date: Dayjs | null,
  time: Dayjs | null
): Dayjs | null {
  if (!date || !time) {
    return null
  }
  return date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0)
}

export function calcPayAmount(ratePerHour: number, minutes: number): string {
  return (Math.round(ratePerHour * (minutes / 60) * 100) / 100).toFixed(2)
}
